import Api from "./api";
import { getServerDate } from "../utils/storage/date";
import DutyStatusLog from "../model/DutyStatusLog";
import { getCurrentVehicle } from "../utils/device";
import { getEldProviderName } from "../utils/helpers";
import * as logger from "../utils/logger";
import Service from "./base";
import Sync from "../sync/sync";
import scheduler from "../utils/scheduler";
import { SecurityService } from "../utils/security";
import constants from "../constants";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import idSerializer from "geotab-uuid";
import { DriveStore } from "../store/reducers";
import { isInCanada, CanadaEldOperatingSystems, type THosMalfunction } from "hos-tools";
import {DutyStatusLogService} from "../model/DutyStatusLogs";
import { UserHosRuleSetService } from "../model/UserHosRuleSets";
import { DataActions } from "../store/data/actions";
import { isIphone } from "../utils/native/nativeHelpers";

const SEVEN_DAYS = 1000 * 60 * 60 * 24 * 7;

interface IELDTransferDetails {
    driverId: string;
    fromDate: string;
    toDate: string;
    transferMode: string;
    comment: string;
    providerName: string;
    isTest: boolean;
    validateOnly: boolean;
    recipientEmailAddress?: string;
    os?: string;
    serverUrl: string;
}

export class DTCMonitor extends Service {

    readonly sync: Sync;
    store: DriveStore;
    api: Api;
    serverDateTime: number;
    userHosRuleSetService: UserHosRuleSetService;

    constructor(
        store: DriveStore,
        api: Api,
        sync: Sync,
        private readonly dutyStatusLogService: DutyStatusLogService,
        userHosRuleSetService: UserHosRuleSetService,
        private readonly securityService: SecurityService
    ) {
        super();
        this.store = store;
        this.api = api;
        this.sync = sync;
        this.store = store;
        this.userHosRuleSetService = userHosRuleSetService;
        this.serverDateTime = getServerDate().getTime();
    }

    destroy() { }

    runDTCTransfer() {
        let currentUser = this.api.getUser();

        let currentVehicle = getCurrentVehicle();
        this.serverDateTime = getServerDate().getTime();

        if (currentVehicle === null || currentVehicle.id === "NoDeviceId") {
            this.rescheduleDtcMonitor(currentUser, constants.DTC_MONITOR_NO_VEHICLE_RETRY);
            return Promise.resolve();
        }

        this.rescheduleDtcMonitor(currentUser, constants.DTC_MONITOR, false);
        let currentVehicleId = currentVehicle.id;

        let dataTransferLogs = this.getDTCLogs(currentVehicleId, currentUser);
        if (dataTransferLogs.length === 0) {
            return this.checkELDTransfer(this.api, this.api.getUser(), currentVehicleId);
        }

        let latestDTCLog = dataTransferLogs[0];

        let isSameDay = (oldDate: Date, newDate: Date) => {
            return oldDate.toDateString() === newDate.toDateString();
        };

        // If there is already a DTC log created for today, don't run ELD transfer.
        if (isSameDay(latestDTCLog.getDate(), new Date(this.serverDateTime))) {
            this.clearDTCNextRun(currentUser);
        } else if (latestDTCLog.isDTCSystemClearLog() && this.isDiagnosticNoOfDaysOld(latestDTCLog, SEVEN_DAYS)) {
            return this.checkELDTransfer(this.api, currentUser, currentVehicleId);
        } else if (latestDTCLog.isDataTransferComplianceMalfunctionLog()) {
            return this.checkELDTransfer(this.api, currentUser, currentVehicleId, false);
        } else if (latestDTCLog.isDataTransferComplianceDiagnosticLog()) {
            let diagnosticAttemptDays = [latestDTCLog.getDate()];
            return this.checkELDTransfer(this.api, currentUser, currentVehicleId, false).catch(() => {
                for (let i = 1; i < dataTransferLogs.length; i++) {
                    let previousDTCLog = dataTransferLogs[i];
                    if (!previousDTCLog.isDataTransferComplianceDiagnosticLog()) {
                        break;
                    }
                    let currentDTCLogDate = diagnosticAttemptDays[diagnosticAttemptDays.length - 1];
                    let previousDTCLogDate = previousDTCLog.getDate();
                    if (!isSameDay(currentDTCLogDate, previousDTCLogDate)) {
                        diagnosticAttemptDays.push(previousDTCLogDate);
                    }
                    if (diagnosticAttemptDays.length === 3) {
                        return this.addDTCMalfunction("Malfunction", currentVehicleId);
                    }
                }
                return this.addDTCMalfunction("Diagnostic", currentVehicleId);
            });
        }
        return Promise.resolve();
    }

    clearDTCNextRun(currentUser: IUser | null) {
        let apiUserId = currentUser ? currentUser.id : undefined;
        if (apiUserId) {
            let dtcNextRun = this.store.getState().data.dtcNextRun[apiUserId];
            if (dtcNextRun) {
                this.store.dispatch(DataActions.clearDtcNextRun(apiUserId));
            }
        }
    }

    addDTCMalfunction = (malfunctionType: THosMalfunction, vehicleId: string) => {
        if (vehicleId !== "NoDeviceId") {

            // Do not create DataTransferCompliance diagnostic/malfunction when under CarrierExemption ruleset.
            const isCarrierExempt = this.userHosRuleSetService.isLatestRulesetCarrierExemption();
            const isSystemClear = malfunctionType === "SystemDiagnosticClear";

            if (isCarrierExempt && !isSystemClear) {
                logger.log(logger.Source.SERVICES, `Skipping DTC ${ malfunctionType } log creation for Carrier Exemption user`, this.api && this.api.getUser());
                return Promise.resolve();
            }

            let newLog = new DutyStatusLog({
                id: idSerializer.get(),
                dateTime: getServerDate().toISOString(),
                status: "DataTransferCompliance",
                malfunction: malfunctionType,
                device: { id: vehicleId },
                driver: { id: "NoUserId" },
                origin: "Automatic"
            });
            return this.sync.dutyStatusLogs.addItem(newLog, false);
        }
        return Promise.resolve();
    };

    getDTCLogs(currentVehicleId: string, currentUser: IUser | null): DutyStatusLog[] {
        let logs: DutyStatusLog[] = [];
        let apiUserId = currentUser ? currentUser.id : undefined;
        logs = this.dutyStatusLogService.getHOSLogs(true, apiUserId, true, true, true).filter(log => log.getDeviceId() === currentVehicleId && log.isDTCLog());
        return logs;
    }

    getHost() {
        return geotab.location.host;
    }

    private rescheduleDtcMonitor(currentUser: IUser | null, intervalMs: number, persistNextRun = true) {
        if (!currentUser) {
            return;
        }
        scheduler.setInterval("@dtcMonitor", intervalMs, currentUser.name);
        if (persistNextRun) {
            this.store.dispatch(
                DataActions.setDtcNextRun({ [currentUser.id]: new Date(getServerDate().getTime() + intervalMs).toISOString() })
            );
        }
    }

    private checkELDTransfer(api: Api, currentUser: IUser | null, currentVehicleId: string, addDiagnostic = true) {
        if (window.DRIVE_DEBUG && !window.DRIVE_DEBUG.ENABLE_ELD_TRANSFER && window.location.host.indexOf("127.0.0.1") !== -1) {
            return Promise.resolve();
        }

        if (!currentUser || !currentUser.isDriver) {
            return Promise.resolve();
        }

        // Check if user has HOS clearance before executing TransferEld
        if (!this.securityService.can("attemptHos", currentUser.id)) {
            this.clearDTCNextRun(currentUser);
            return Promise.resolve();
        }

        let isCanada = isInCanada(this.userHosRuleSetService.getLatestHosRuleSetIdShortened());

        let serverDate = getServerDate().toISOString();

        let credentials = api.getCurrentCredentials();
        let database = credentials ? credentials.credentials.database : "";

        let params: IELDTransferDetails = {
            driverId: currentUser.id,
            fromDate: serverDate,
            toDate: serverDate,
            transferMode: "WebServices",
            comment: "Transfer ELD",
            providerName: getEldProviderName(database, window.whitelabel, isCanada),
            isTest: true,
            validateOnly: this.store.getState().data.debugMode, // ValidateOnly = true if debug mode is active
            os: isIphone() ? CanadaEldOperatingSystems.IOS : CanadaEldOperatingSystems.Android,
            serverUrl: this.getHost()
        };

        if (isCanada) {
            params.transferMode = "CanadaEmail";
            params.recipientEmailAddress = constants.CANADA_RECIPIENT_EMAIL_ADDRESS;
        }

        return api.callPromise("TransferEld", params).then(result => {
            logger.log(logger.Source.SERVICES, `Check ELD Transfer completed, ELD Response Status: ${ result.status }`, api && api.getUser());
            this.clearDTCNextRun(currentUser);
            if (window.DRIVE_DEBUG && window.DRIVE_DEBUG.ELD_DTC_TRANSFER_FAIL) {
                return Promise.reject(new Error("DEBUG Mode: Failure doing an ELD Transfer"));
            } else {
                return this.addDTCMalfunction("SystemDiagnosticClear", currentVehicleId);
            }
        }).catch(err => {
            let eldErrorMessage = `Failure doing an ELD Transfer ${ err.message }`;
            logger.error(logger.Source.SERVICES, eldErrorMessage);
            this.clearDTCNextRun(currentUser);
            if (addDiagnostic) {
                return this.addDTCMalfunction("Diagnostic", currentVehicleId);
            }
            return Promise.reject(new Error(eldErrorMessage));
        });
    }

    private isDiagnosticNoOfDaysOld(latestLog: DutyStatusLog, numberOfDaysOld: number): boolean {
        let latestLogDateTime: number;
        latestLogDateTime = new Date(latestLog.dateTime).getTime();
        return (latestLogDateTime < this.serverDateTime) && ((this.serverDateTime - latestLogDateTime) >= numberOfDaysOld);
    }
}
